import React, { useEffect, useState, useRef } from "react";
import { Auth } from "aws-amplify";
import "./PremiumDashboard.css";

const SimpleTooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  const getTooltipPosition = () => {
    if (!containerRef.current) return { left: 'calc(100% + 10px)', transform: 'none' };

    const rect = containerRef.current.getBoundingClientRect();
    const tooltipWidth = 350;
    const rightSpace = window.innerWidth - rect.right;

    if (rightSpace < tooltipWidth + 20) {
      return {
        right: 'calc(100% + 10px)',
        left: 'auto',
        transform: 'none'
      };
    }

    return {
      left: 'calc(100% + 10px)',
      right: 'auto',
      transform: 'none'
    };
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShow(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShow(false);
    }, 300);
  };

  const handleTooltipEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleTooltipLeave = () => {
    setShow(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
          padding: '4px',
          backgroundColor: show ? '#f0f8ff' : 'transparent',
          cursor: 'help'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {show && (
        <div
          style={{
            position: 'absolute',
            ...getTooltipPosition(),
            top: '0px',
            backgroundColor: '#2a2a2a',
            color: 'white',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '12px',
            minWidth: '200px',
            maxWidth: '350px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            zIndex: 99999,
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            border: '1px solid #555',
            pointerEvents: 'auto',
            userSelect: 'text',
            cursor: 'text'
          }}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
        >
          {/* <div style={{
            fontWeight: '600',
            marginBottom: '6px',
            color: '#90CDF4',
            fontSize: '10px',
            textAlign: 'center'
          }}>
            Click text below to select & copy
          </div> */}
          <div
            style={{
              fontSize: '12px',
              lineHeight: '1.4',
              cursor: 'text',
              padding: '1px',
              borderRadius: '1px',
              backgroundColor: 'rgba(255,255,255,0.1)'
            }}
            onClick={(e) => {
              const range = document.createRange();
              range.selectNodeContents(e.target);
              const selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
              e.target.style.backgroundColor = 'rgba(144, 205, 244, 0.3)';
              setTimeout(() => {
                e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }, 200);
            }}
          >
            {content || children}
          </div>
        </div>
      )}
    </div>
  );
};

export default function PremiumDashboard() {
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [purchasedPlan, setPurchasedPlan] = useState(null);

  const getToken = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();
      const jwt = session.getIdToken().getJwtToken();
      const usernameOrEmail = user.username.includes("Google_")
        ? user.attributes.email
        : user.username;
      return [usernameOrEmail, jwt];
    } catch (error) {
      await Auth.signOut();
      return [null, null];
    }
  };

  const fetchPaymentHistory = async () => {
    const [username] = await getToken();
    if (!username) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(
        "https://i3ppb217bi.execute-api.us-east-1.amazonaws.com/dev/getPaymentHistory",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username }),
        }
      );
      const data = await response.json();

      if (response.ok) {
        const body =
          data.body && typeof data.body === "string" ? JSON.parse(data.body) : data;
        console.log('Subscription data:', body.subscription); // Debug log
        // Reverse the payment history array to show most recent transactions first
        setPaymentHistory((body.paymentHistory || []).reverse());
        setSubscription(body.subscription || null);
        
        // Check for plan in multiple possible locations
        const plan = body.subscription?.purchased_plan || 
                   body.subscription?.purchasedPlan ||
                   body.subscription?.plan_id ||
                   (body.paymentHistory && body.paymentHistory[0]?.plan_id) ||
                   "Free";
        console.log('Setting purchased plan to:', plan); // Debug log
        setPurchasedPlan(plan);
      } else {
        setError(data.error || "Failed to fetch payment history.");
      }
    } catch (err) {
      setError("An error occurred while fetching payment history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      : "N/A";

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "N/A";

  return (
    <div className="premium-dashboard-container">
      {/* Always show subscription section structure during loading */}
      {(loading || (subscription &&
        subscription.status &&
        subscription.status.toLowerCase() !== 'inactive' &&
        subscription.status.toLowerCase() !== 'expired')) && (
          <div className="subscription-card">
            <h2 className="subscription-title">Your Subscription</h2>
            {loading ? (
              <div className="subscription-details">
                <div className="detail-item">
                  <span className="detail-label">Current Plan</span>
                  <div className="loading-placeholder">Loading...</div>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <div className="loading-placeholder">Loading...</div>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Expiry Date</span>
                  <div className="loading-placeholder">Loading...</div>
                </div>
              </div>
            ) : error ? (
              <p className="subscription-error">{error}</p>
            ) : subscription ? (
              <div className="subscription-details">
                <div className="detail-item">
                  <span className="detail-label">Current Plan</span>
                  <span className={`plan-badge ${(purchasedPlan || 'free').toLowerCase().replace(/ /g, '-')}`}>
                    {purchasedPlan ? capitalize(purchasedPlan) : 'Free'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${subscription.status.toLowerCase()}`}>
                    {capitalize(subscription.status)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Expiry Date</span>
                  <span className="detail-value">{formatDate(subscription.endDate)}</span>
                </div>
              </div>
            ) : (
              <p className="subscription-info">No active subscription found.</p>
            )}
          </div>
        )}

      <div className="payment-history-card">
        <h2 className="payment-history-title">Payment History</h2>
        {loading ? (
          <div className="table-container">
            <table className="payment-history-table">
              <thead>
                <tr>
                  {/* <th>Payment ID</th> */}
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Currency</th>
                  {/* <th>Payment Type</th> */}
                  <th>Paid On</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                  <th>Users</th>
                  {/* <th>Timestamp</th> */}
                </tr>
              </thead>
              <tbody>
                {/* Show 3 placeholder rows during loading */}
                {[1, 2, 3].map((_, index) => (
                  <tr key={index}>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    <td className="truncate"><div className="loading-placeholder">Loading...</div></td>
                    {/* <td className="truncate"><div className="loading-placeholder">Loading...</div></td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : paymentHistory.length > 0 ? (
          <div className="table-container">
            <table className="payment-history-table">
              <colgroup>
                <col style={{ width: '15%' }} /> {/* Plan */}
                <col style={{ width: '10%' }} />  {/* Amount */}
                <col style={{ width: '8%' }} />   {/* Currency */}
                <col style={{ width: '20%' }} />  {/* Paid On */}
                <col style={{ width: '20%' }} />  {/* Expiry Date */}
                <col style={{ width: '12%' }} />  {/* Status */}
                <col style={{ width: '25%' }} />  {/* Users */}
              </colgroup>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Currency</th>
                  <th>Paid On</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                  <th>Users</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment, index) => (
                  <tr key={index}>
                    <td>
                      <div className="table-cell-content">
                        <SimpleTooltip content={capitalize(payment.plan_id)}>
                          <span className="detail-value">{capitalize(payment.plan_id)}</span>
                        </SimpleTooltip>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <span className="detail-value">${payment.amount.toFixed(2)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <span className="detail-value">{payment.currency.toUpperCase()}</span>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <span className="detail-value">{formatDate(payment.startDate)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <span className="detail-value">{formatDate(payment.endDate)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <span className={`status-badge ${payment.status.toLowerCase()}`}>
                          {capitalize(payment.status)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <SimpleTooltip content={payment.group_students ? payment.group_students.join(", ") : "N/A"}>
                          <span className="detail-value">
                            {payment.group_students ? payment.group_students.join(", ") : "N/A"}
                          </span>
                        </SimpleTooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="payment-history-info">No payment history found.</p>
        )}
      </div>
    </div>
  );
}